import nacl from 'tweetnacl';
import * as util from 'tweetnacl-util';

/**
 * End-to-End Encryption Service
 * Uses TweetNaCl (NaCl) for secure message encryption
 * Keys are stored in browser localStorage and NEVER sent to server
 */

const STORAGE_KEY = 'tapestry_encryption_keypair';

export interface KeyPair {
  publicKey: string; // Base64 encoded
  secretKey: string; // Base64 encoded
}

/**
 * Generate a new encryption keypair for the user
 * Call this once during first login/signup
 */
export function generateKeyPair(): KeyPair {
  const keyPair = nacl.box.keyPair();
  return {
    publicKey: util.encodeBase64(keyPair.publicKey),
    secretKey: util.encodeBase64(keyPair.secretKey)
  };
}

/**
 * Store keypair in localStorage
 */
export function storeKeyPair(keyPair: KeyPair): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(keyPair));
}

/**
 * Retrieve keypair from localStorage
 */
export function getKeyPair(): KeyPair | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  return JSON.parse(stored);
}

/**
 * Check if user has encryption keys set up
 */
export function hasKeyPair(): boolean {
  return localStorage.getItem(STORAGE_KEY) !== null;
}

/**
 * Encrypt a message for a recipient
 * @param message - Plain text message
 * @param recipientPublicKey - Recipient's public key (base64)
 * @param senderSecretKey - Sender's secret key (base64)
 * @returns Encrypted message (base64) with nonce
 */
export function encryptMessage(
  message: string,
  recipientPublicKey: string,
  senderSecretKey: string
): string {
  try {
    const messageUint8 = util.decodeUTF8(message);
    const nonce = nacl.randomBytes(nacl.box.nonceLength);
    const recipientPubKey = util.decodeBase64(recipientPublicKey);
    const senderSecKey = util.decodeBase64(senderSecretKey);

    const encrypted = nacl.box(
      messageUint8,
      nonce,
      recipientPubKey,
      senderSecKey
    );

    if (!encrypted) {
      throw new Error('Encryption failed');
    }

    // Combine nonce + encrypted message for transmission
    const fullMessage = new Uint8Array(nonce.length + encrypted.length);
    fullMessage.set(nonce);
    fullMessage.set(encrypted, nonce.length);

    return util.encodeBase64(fullMessage);
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt message');
  }
}

/**
 * Decrypt a message from a sender
 * @param encryptedMessage - Encrypted message with nonce (base64)
 * @param senderPublicKey - Sender's public key (base64)
 * @param recipientSecretKey - Recipient's secret key (base64)
 * @returns Decrypted plain text message
 */
export function decryptMessage(
  encryptedMessage: string,
  senderPublicKey: string,
  recipientSecretKey: string
): string {
  try {
    const messageWithNonce = util.decodeBase64(encryptedMessage);
    const nonce = messageWithNonce.slice(0, nacl.box.nonceLength);
    const message = messageWithNonce.slice(nacl.box.nonceLength);

    const senderPubKey = util.decodeBase64(senderPublicKey);
    const recipientSecKey = util.decodeBase64(recipientSecretKey);

    const decrypted = nacl.box.open(
      message,
      nonce,
      senderPubKey,
      recipientSecKey
    );

    if (!decrypted) {
      throw new Error('Decryption failed - message may be corrupted or keys are incorrect');
    }

    return util.encodeUTF8(decrypted);
  } catch (error) {
    throw new Error('Failed to decrypt message');
  }
}

/**
 * Initialize encryption for a new user
 * Generates and stores keypair, returns public key to share with server
 */
export function initializeEncryption(): string {
  if (hasKeyPair()) {
    const keyPair = getKeyPair();
    return keyPair!.publicKey;
  }

  const keyPair = generateKeyPair();
  storeKeyPair(keyPair);
  return keyPair.publicKey;
}

/**
 * Clear encryption keys (logout)
 */
export function clearKeys(): void {
  localStorage.removeItem(STORAGE_KEY);
}
